// Experiments: every experiment the registry knows, in one place, with the
// guardrail numbers beside the dials that move it.
//
// The prompt-section console answers "what is this ONE injection set to"; the
// A/B page answers "did this ONE experiment move the funnel". This page is the
// register: the whole population of experiments, of every kind, with the
// operational strip (calls, cost, errors, p95) that says whether an arm is
// safe to keep running, the guardrail breaches the worker found on its last
// tick, the parity counts that say the served arm matched the assigned one,
// the lifecycle ledger, and a lookup for "what was THIS project on".
//
// Everything but the lookup reads one worker-built snapshot
// (`GET /api/admin/experimentation`), rebuilt on the half-hourly tick. No
// polling: `computing` renders as a sentence, and the next visit sees the
// snapshot. An UNMEASURED value prints "-", never 0 (`lib/experimentation.ts`).
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { Badge, Button, TableExport } from "@recruiter/shared";
import type {
  AdminExperimentRecord,
  AdminExperimentationResponse,
  AdminGuardrailBreach,
  AdminLifecycleEvent,
} from "@recruiter/shared";

import { AreaBoundary } from "../components/area-gate";
import { ExperimentBoard } from "../components/experiment-board";
import { ExperimentRegister } from "../components/experiment-register";
import { RestoreDefaults } from "../components/restore-defaults";
import { LoadingState, errorMessage } from "../components/states";
import { killExperiment } from "../lib/api";
import { effectiveEmployeeEmail } from "../lib/identity";
import { usePersistedState } from "../lib/persisted-state";
import {
  type RegisterFilters,
  type RegisterSearch,
  type RegisterSort,
  type SavedView,
  allocatableKind,
  formatTransition,
  parityTone,
  parseProjectId,
} from "../lib/experimentation";
import { formatDateTime } from "../lib/format";
import {
  adminKeys,
  experimentationLifecycleQuery,
  experimentationQuery,
  projectExperimentsQuery,
} from "../lib/queries";
import "./experiments.css";

const LIFECYCLE_LIMIT = 50;

/** Where the board/table preference lives. App-wide unique, per the view-state
 *  key rules. */
const BOARD_VIEW_KEY = "experiments/view/board";

type Props = {
  /** Reached through the old /ab-tests path: lead with where the funnel
   *  readout went, so the bookmark still answers the question it used to. */
  outcomesHint?: boolean;
  /** The register's whole view, held in the URL so it is a link. */
  filters: RegisterFilters;
  sort: RegisterSort;
  view: SavedView | undefined;
  onSearchChange: (next: RegisterSearch) => void;
  /** May this operator kill an experiment? `system_admin` at full. */
  canAct: boolean;
  /** Has the grant query answered? A sentence about somebody's access must not
   *  be asserted before it has, or a full holder reads "read only" on every
   *  cold load and watches it swap under them. */
  accessKnown: boolean;
};

export function ExperimentsPage({
  outcomesHint = false,
  filters,
  sort,
  view,
  onSearchChange,
  canAct,
  accessKnown,
}: Props) {
  const snapshot = useQuery(experimentationQuery());
  const queries = useQueryClient();
  // Which view. Persisted, because an operator who prefers one is going to
  // prefer it tomorrow too, and a page that resets to the other on every visit
  // makes them re-choose forever.
  const [board, setBoard] = usePersistedState<boolean>(BOARD_VIEW_KEY, true);
  const me = effectiveEmployeeEmail(null) ?? "";

  // `/` focuses the search box. Skipped while the caret is already in a field,
  // so typing a slash into the search box types a slash.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const active = document.activeElement;
      if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
      const box = document.querySelector<HTMLInputElement>("[data-register-search]");
      if (!box) return;
      event.preventDefault();
      box.focus();
      box.select();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // One request per key rather than one bulk route: a partial failure then
  // stops the keys it could not reach and leaves the ones it did stopped,
  // which is the right way round for a stop-loss.
  const kill = useMutation({
    mutationFn: async ({ keys, reason }: { keys: readonly string[]; reason: string }) => {
      const rows = snapshot.data?.experiments ?? [];
      const failures: string[] = [];
      for (const key of keys) {
        const record = rows.find((r) => r.key === key);
        // Every registry kind is allocatable now, `logic` and `config`
        // included, so this only refuses a kind this build does not know.
        const kind = record ? allocatableKind(record.kind) : null;
        if (!record || !kind) {
          failures.push(`${key} (no console dial for kind ${record?.kind ?? "unknown"})`);
          continue;
        }
        try {
          await killExperiment(key, { kind, reason });
        } catch (err) {
          failures.push(`${key} (${err instanceof Error ? err.message : String(err)})`);
        }
      }
      if (failures.length > 0) throw new Error(`Not stopped: ${failures.join("; ")}`);
    },
    onSettled: () => {
      void queries.invalidateQueries({ queryKey: adminKeys.experimentation });
    },
  });

  if (snapshot.isPending) return <LoadingState label="Reading the experiments…" />;
  if (snapshot.isError) {
    return (
      <AreaBoundary
        area="system_admin"
        error={snapshot.error}
        onRetry={() => void snapshot.refetch()}
      />
    );
  }

  const data = snapshot.data;
  const computing = data.state !== "ready";

  return (
    <div className="exp-page">
      <header className="exp-page__head">
        <h1 className="ad-page__title">Experiments</h1>
        <p className="exp-page__sub">
          Every experiment the registry derives, of every kind, with the guardrail strip that says
          whether an arm is safe to keep running. Built by the worker on its half-hourly tick
          {data.generatedAt ? `; this snapshot is from ${formatDateTime(data.generatedAt)}.` : "."}
        </p>
        {/* The sweep belongs HERE and not on the System Admin dial card, which lists
            three of the ninety-six: "all experiments" is a statement about this page's
            population. The card carries a link to it rather than a second button.

            NOT on the Playground entry. `/ab-tests` renders this same page (the A/B
            readout moved into each experiment's Outcomes tab), so a reader who clicked
            "Playground" to compare two arms across the funnel arrived at a control that
            rewrites every allocation in the environment. That is a different job on a
            page they did not ask for, and the most destructive control in the console
            is the last one that should greet somebody who came to read a number.
            `outcomesHint` is exactly "reached through the old path", so it is the right
            thing to gate on: the Experiments page proper still carries it. */}
        {outcomesHint ? null : <RestoreDefaults canAct={canAct} />}
      </header>

      {computing ? (
        <p className="exp-page__computing" role="status">
          Not computed yet, the worker builds it on its half-hourly tick.
        </p>
      ) : null}

      {outcomesHint ? <OutcomesHint experiments={data.experiments} /> : null}
      {/* The BOARD is the default, and the table is a click away.
          A table answers "which of these ninety-seven is worth my attention".
          The job an operator actually sits down to do is "read what this thing
          says, then move its dial", and that job wants a card, the control on
          the card, and the text beside it - which is the shape the prompt-
          section console had and the register lost. */}
      {board ? (
        <ExperimentBoard
          records={data.experiments}
          canAct={canAct}
          accessKnown={accessKnown}
          onShowTable={() => setBoard(false)}
        />
      ) : (
        <>
          <div className="exp-page__viewswitch">
            <Button size="sm" variant="ghost" tone="neutral" onClick={() => setBoard(true)}>
              Board view
            </Button>
          </div>
          <ExperimentRegister
            data={data}
            filters={filters}
            sort={sort}
            view={view}
            me={me}
            canAct={canAct}
            onSearchChange={onSearchChange}
            onKill={async (rows, reason) => {
              await kill.mutateAsync({ keys: rows.map((r) => r.key), reason });
            }}
          />
        </>
      )}
      <GuardrailsPanel data={data} />
      <ParityPanel parity={data.parity} />
      <TimelinePanel />
      <ProjectLookup />
    </div>
  );
}

/** Where the funnel readout went, for a reader who arrived through /ab-tests:
 *  one link per product experiment, straight to its Outcomes tab. */
function OutcomesHint({ experiments }: { experiments: readonly AdminExperimentRecord[] }) {
  const product = experiments.filter((e) => e.kind === "ui");
  return (
    <section className="exp-section exp-section--hint" aria-labelledby="exp-outcomes-title">
      <h2 className="exp-section__title" id="exp-outcomes-title">
        Outcomes moved
      </h2>
      <p className="exp-section__sub">
        The A/B readout (both arms across the funnel steps) is now the Outcomes tab of each product
        experiment's own page.
        {product.length === 0 ? " No product experiment is in this snapshot yet." : ""}
      </p>
      {product.length > 0 ? (
        <ul className="exp-parity">
          {product.map((e) => (
            <li className="exp-parity__row" key={e.key}>
              <Link
                className="ad-link"
                to="/experiments/$key"
                params={{ key: e.key }}
                search={{ tab: "outcomes" }}
              >
                {e.label || e.key}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function GuardrailsPanel({ data }: { data: AdminExperimentationResponse }) {
  return (
    <section className="exp-section" aria-labelledby="exp-guardrails-title">
      <h2 className="exp-section__title" id="exp-guardrails-title">
        Guardrails
      </h2>
      <p className="exp-section__facts">
        <Badge tone={data.guardrailsEnabled ? "success" : "warning"}>
          {data.guardrailsEnabled ? "enabled" : "disabled"}
        </Badge>{" "}
        On a breach: <code>{data.guardrailAction}</code>
      </p>
      {data.breaches.length === 0 ? (
        <p className="exp-section__empty">No breaches on the last tick.</p>
      ) : (
        <ul className="exp-breaches">
          {data.breaches.map((b) => (
            <BreachRow key={`${b.experiment}:${b.arm}:${b.guardrail}`} breach={b} />
          ))}
        </ul>
      )}
    </section>
  );
}

function BreachRow({ breach }: { breach: AdminGuardrailBreach }) {
  return (
    <li className="exp-breaches__row">
      <Badge tone="danger">{breach.guardrail}</Badge>
      <span>
        <code>{breach.experiment}</code> arm <code>{breach.arm}</code>: {breach.signal} at{" "}
        {breach.armValue} against default {breach.defaultValue}. Action: {breach.action}.
      </span>
    </li>
  );
}

function ParityPanel({ parity }: { parity: Record<string, number> }) {
  const entries = Object.entries(parity).sort(([a], [b]) => a.localeCompare(b));
  return (
    <section className="exp-section" aria-labelledby="exp-parity-title">
      <h2 className="exp-section__title" id="exp-parity-title">
        Parity
      </h2>
      <p className="exp-section__sub">
        Disagreements between what was assigned and what was served. Zero is the healthy reading.
      </p>
      {entries.length === 0 ? (
        <p className="exp-section__empty">No parity counts in this snapshot.</p>
      ) : (
        <ul className="exp-parity">
          {entries.map(([name, count]) => (
            <li className="exp-parity__row" key={name}>
              <code>{name}</code>
              <Badge tone={parityTone(count)}>{count.toLocaleString()}</Badge>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TimelinePanel() {
  const lifecycle = useQuery(experimentationLifecycleQuery({ limit: LIFECYCLE_LIMIT }));
  // Newest first regardless of what the route returns, so the top row is
  // always the latest move even if the order upstream changes.
  const events = [...(lifecycle.data?.events ?? [])].sort((a, b) =>
    b.createdAt.localeCompare(a.createdAt),
  );
  return (
    <section className="exp-section" aria-labelledby="exp-timeline-title">
      <h2 className="exp-section__title" id="exp-timeline-title">
        Timeline
      </h2>
      {lifecycle.isPending ? <LoadingState label="Reading the ledger…" /> : null}
      {lifecycle.isError ? (
        <p className="exp-section__error">{errorMessage(lifecycle.error)}</p>
      ) : null}
      {lifecycle.data && events.length === 0 ? (
        <p className="exp-section__empty">Nothing has changed state yet.</p>
      ) : null}
      {events.length > 0 ? (
        <div className="exp-table-wrap">
          <table className="exp-table">
            <TableExport name="experiment-events" label="state changes" />
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Entity</th>
                <th scope="col">Transition</th>
                <th scope="col">Actor</th>
                <th scope="col">Reason</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <TimelineRow key={e.id} event={e} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function TimelineRow({ event }: { event: AdminLifecycleEvent }) {
  return (
    <tr>
      <td className="exp-table__when">{formatDateTime(event.createdAt)}</td>
      <td>
        <Badge tone="neutral">{event.entityKind}</Badge> <code>{event.entityKey}</code>
      </td>
      <td>
        <code>{formatTransition(event.fromState, event.toState)}</code>
      </td>
      <td>{event.actorEmail}</td>
      <td>{event.reason}</td>
    </tr>
  );
}

/** Paste a project id, see what it was on. The query is keyed on the SUBMITTED
 *  id, not the typed one, so a half-typed uuid never fires a request. */
function ProjectLookup() {
  const [typed, setTyped] = useState("");
  const [projectId, setProjectId] = useState("");
  const parsed = parseProjectId(typed);
  const lookup = useQuery(projectExperimentsQuery(projectId));

  return (
    <section className="exp-section" aria-labelledby="exp-lookup-title">
      <h2 className="exp-section__title" id="exp-lookup-title">
        Project lookup
      </h2>
      <form
        className="exp-lookup"
        onSubmit={(e) => {
          e.preventDefault();
          if (parsed) setProjectId(parsed);
        }}
      >
        <input
          className="exp-lookup__input"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Project id (uuid)"
          aria-label="Project id"
          spellCheck={false}
        />
        <Button size="sm" tone="accent" type="submit" disabled={!parsed || lookup.isFetching}>
          {lookup.isFetching ? "Looking up…" : "Look up"}
        </Button>
        {typed.trim() && !parsed ? (
          <span className="exp-lookup__hint">That is not a project uuid.</span>
        ) : null}
      </form>

      {lookup.isError ? <p className="exp-section__error">{errorMessage(lookup.error)}</p> : null}
      {lookup.data ? (
        <div className="exp-lookup__result">
          <h3 className="exp-lookup__subtitle">Exposures</h3>
          {lookup.data.exposures.length === 0 ? (
            <p className="exp-section__empty">No A/B exposures recorded.</p>
          ) : (
            <div className="exp-table-wrap">
              <table className="exp-table">
                <TableExport name="user-exposures" label="exposures" />
                <thead>
                  <tr>
                    <th scope="col">Experiment</th>
                    <th scope="col">Assigned</th>
                    <th scope="col">Served</th>
                    <th scope="col">First seen</th>
                  </tr>
                </thead>
                <tbody>
                  {lookup.data.exposures.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <code>{String(row.experiment ?? "-")}</code>
                      </td>
                      <td>{String(row.assignedArm ?? "-")}</td>
                      <td>{String(row.servedArm ?? "-")}</td>
                      <td>
                        {typeof row.firstSeenAt === "string"
                          ? formatDateTime(row.firstSeenAt)
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 className="exp-lookup__subtitle">Approaches</h3>
          {Object.keys(lookup.data.approaches).length === 0 ? (
            <p className="exp-section__empty">No approach picks recorded.</p>
          ) : (
            <ul className="exp-parity">
              {Object.entries(lookup.data.approaches).map(([slot, approach]) => (
                <li className="exp-parity__row" key={slot}>
                  <code>{slot}</code>
                  <Badge tone="neutral">{approach}</Badge>
                </li>
              ))}
            </ul>
          )}

          <h3 className="exp-lookup__subtitle">Injection decisions</h3>
          {lookup.data.decisions.length === 0 ? (
            <p className="exp-section__empty">No injection decisions recorded.</p>
          ) : (
            <div className="exp-table-wrap">
              <table className="exp-table">
                <TableExport name="injection-decisions" label="injection decisions" />
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Agent</th>
                    <th scope="col">Injection</th>
                    <th scope="col">Enrolled</th>
                    <th scope="col">Triggered</th>
                    <th scope="col">Sent</th>
                    <th scope="col">Fired</th>
                    <th scope="col">Turn</th>
                  </tr>
                </thead>
                <tbody>
                  {lookup.data.decisions.map((d) => (
                    <tr key={`${d.turnId}:${d.injectionKey}`}>
                      <td className="exp-table__when">{formatDateTime(d.createdAt)}</td>
                      <td>{d.agent}</td>
                      <td>
                        <code>{d.injectionKey}</code>
                      </td>
                      <td>{d.enrolled ? "yes" : "no"}</td>
                      <td>{d.triggered ? "yes" : "no"}</td>
                      <td>{d.sent ? "yes" : "no"}</td>
                      <td>{d.fired.length ? d.fired.join(", ") : "-"}</td>
                      <td>
                        <code className="exp-table__key">{d.turnId}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
